import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableRow,
	useTheme,
	Box,
	Avatar,
	TablePagination,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { getEmployees, deleteEmployee } from "../../reducers/EmployeeReducer";
import { updateOrCreateUserSetting } from "../../reducers/UserSettingsReducer";
import EnhancedTableToolbar from "./EnhancedTableToolbar";
import PATEditButton from "../../components/Buttons/PATEditButton";
import PATDeleteButton from "../../components/Buttons/PATDeleteButton";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import { AMSModules, employeesView, paginationStyle } from "../../utils/constants";
import {
	PATH_EMPLOYEE_ADD,
	PATH_EMPLOYEE_EDIT,
} from "../../utils/routeConstants";
import {
	formatToBase64ImageUrl,
	getInitials,
	getRowsPerPageOptions,
	mapDictionaryOptions,
} from "../../utils/functions";
import { getAccessPermission } from "../../reducers/PermissionsReducer";
import PATViewButton from "../../components/Buttons/PATViewButton";
import EmployeeCardView from "./EmployeeCardView";

const INITIAL_PAGE = 0;
const INITIAL_PAGE_SIZE = 20;
const INITIAL_SORT_MODEL = [{ field: "id", sort: "asc" }];

export const initialFilterState = {
	employeeNum: "",
	ssn: "",
	lastName: "",
	firstName: "",
	middleName: "",
	status: [],
	separationCode: "",
	department: [],
	titlePosition: [],
	rank: [],
	tagTypes: [],
	section: [],
	cellNo: null,
	assignedCounty: [],
	exactMatch: false,
};

const Employees = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const history = useHistory();

	const { employees, loading, total } = useSelector(
		(state) => state.employees
	);
	const { userSettings } = useSelector((state) => state.userSettings);
	const themeMode = useSelector((state) => state.theme.mode);
	const { userData, isAuthenticated } = useSelector((state) => state.user);
	const userAgency = useSelector((state) => state.user.userAgency);
	const dictionary = useSelector((state) => state.dictionary.dictionary);
	const filtersInfo = useSelector((state) => state.filtersInfo);
	const currentView = useSelector(
		(state) => state.ui.employeeListCurrentView
	);

	const [page, setPage] = useState(INITIAL_PAGE);
	const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
	const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
	const [selectedId, setSelectedId] = useState(null);
	const [columnsOrder, setColumnsOrder] = useState([]);
	const [columnsSize, setColumnsSize] = useState([]);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [filters, setFilters] = useState(filtersInfo.employees || initialFilterState);
	const [settingsLoaded, setSettingsLoaded] = useState(false);
	const currentAgency = useRef(null);

	const raceOptions = mapDictionaryOptions(dictionary.codeRace);
	const genderOptions = mapDictionaryOptions(dictionary.codeSex);
	const ethnicityOptions = mapDictionaryOptions(dictionary.codeEthnicity);
	const contactInfoOptions = mapDictionaryOptions(
		dictionary.codeContactInfoMethodTypes
	);
	const tagTypeOptions = mapDictionaryOptions(dictionary.codePerTagTypes);
	const suffixOptions = mapDictionaryOptions(dictionary.codeNameSuffixes);
	const rankOptions = mapDictionaryOptions(dictionary.codePerRank);
	const countyOptions = mapDictionaryOptions(dictionary.codeCounties);
	const titlePositionOptions = mapDictionaryOptions(
		dictionary.codePerTitlePosition
	);
	const departmentOptions = mapDictionaryOptions(
		dictionary.codePerDepartment
	);
	const sectionOptions = mapDictionaryOptions(dictionary.codePerSection);
	const employmentOptions = mapDictionaryOptions(
		dictionary.codePerEmploymentStatuses
	);

	const employeePermission = getAccessPermission(
		"employees",
		"Employee Record",
		"any"
	);
	const canReadEmployees = !!employeePermission?.Read;
	const canEditEmployees = !!employeePermission?.Edit;
	const canDeleteEmployees = !!employeePermission?.Delete;

	useEffect(() => {
		if (!settingsLoaded || !isAuthenticated) return;

		const { field, sort } = sortModel[0] || {};
		if (field && sort) {
			const isAgencyChanged = currentAgency.current !== userAgency;
			if (isAgencyChanged) {
				resetFilters();
				currentAgency.current = userAgency;
			}
			dispatch(
				getEmployees(page, pageSize, field, sort, isAgencyChanged ? initialFilterState : filters, userAgency)
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		dispatch,
		page,
		pageSize,
		sortModel,
		filters,
		settingsLoaded,
		isAuthenticated,
		userAgency,
	]);

	const baseColumns = useMemo(
		() => [
			{
				field: "thumbnail",
				headerName: "Thumbnail",
				width: 80,
				sortable: false,
				renderCell: ({ row }) => {
					let imageUrl = "";

					if (row.thumbnail?.imageData?.byteLength) {
						const base64 = formatToBase64ImageUrl(
							row.thumbnail.imageData
						);
						if (base64) imageUrl = base64;
					}

					return imageUrl ? (
						<img
							src={imageUrl}
							alt="Thumbnail"
							style={{
								width: 36,
								height: 36,
								borderRadius: "50%",
								objectFit: "cover",
							}}
						/>
					) : (
						<Avatar
							sx={{
								width: 36,
								height: 36,
								fontSize: 14,
								bgcolor: "#90caf9",
							}}
						>
							{getInitials(row.firstName, row.lastName)}
						</Avatar>
					);
				},
			},
			{
				field: "employeeNum",
				headerName: "ID",
				width: 150,
				sortable: true,
			},
			{
				field: "firstName",
				headerName: "First Name",
				width: 150,
				sortable: true,
			},
			{
				field: "lastName",
				headerName: "Last Name",
				width: 150,
				sortable: true,
			},
			{
				field: "middleName",
				headerName: "Middle Name",
				width: 150,
				sortable: true,
			},
			{
				field: "rankInfo.description",
				headerName: "Rank",
				width: 100,
				sortable: true,
			},
			{
				field: "titlePositionInfo.description",
				headerName: "Title/Position",
				width: 100,
				sortable: false,
			},
			{
				field: "sectionInfo.description",
				headerName: "Section",
				width: 100,
			},
			{
				field: "departmentInfo.description",
				headerName: "Department/Division",
				width: 100,
			},
			{
				field: "actions",
				headerName: "Actions",
				type: "actions",
				width: 120,
				align: "center",
				sortable: false,
				renderCell: ({ row }) =>
					row.id ? (
						<Table>
							<TableBody>
								<TableRow>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										{canEditEmployees && (
											<PATEditButton
												tooltipText="Edit Employee"
												onClick={() =>
													history.push(
														`${PATH_EMPLOYEE_EDIT}${row.id}`
													)
												}
												size="20px"
											/>
										)}
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										{canReadEmployees &&
											!canEditEmployees && (
												<PATViewButton
													tooltipText="View Employee Details"
													onClick={() =>
														history.push({
															pathname: `${PATH_EMPLOYEE_EDIT}${row.id}`,
															search: "?readOnly=true",
														})
													}
													size="20px"
												/>
											)}
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										{canDeleteEmployees && (
											<PATDeleteButton
												tooltipText="Delete Employee"
												onClick={() => {
													setSelectedId(row.id);
													setOpenDeleteDialog(true);
												}}
												size="20px"
											/>
										)}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					) : null,
			},
		],
		[history, canDeleteEmployees, canEditEmployees, canReadEmployees]
	);

	const columns = useMemo(() => {
		const hasOrder = columnsOrder.length > 0;
		const hasSizes = Object.keys(columnsSize).length > 0;

		if (!hasOrder && !hasSizes) return baseColumns;

		let baseOrderedColumns;

		if (hasOrder) {
			const ordered = [
				"thumbnail",
				...columnsOrder.filter((field) => field !== "thumbnail"),
			]
				.map((field) => baseColumns.find((col) => col.field === field))
				.filter(Boolean);

			const remaining = baseColumns.filter(
				(col) => !ordered.some((o) => o.field === col.field)
			);

			const actionsIndex = ordered.findIndex(
				(col) => col.field === "actions"
			);
			const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

			baseOrderedColumns = [
				...ordered.slice(0, insertAt),
				...remaining,
				...ordered.slice(insertAt),
			];
		} else {
			baseOrderedColumns = baseColumns;
		}

		const finalColumns = baseOrderedColumns.map((col) => ({
			...col,
			width: columnsSize[col.field] ?? col.width ?? 100,
		}));

		return finalColumns;
	}, [baseColumns, columnsOrder, columnsSize]);

	const allColumnFields = baseColumns.map((col) => col.field);
	useEffect(() => {
		const defaultVisibility = {};
		currentAgency.current = userAgency;
		allColumnFields.forEach((field) => {
			defaultVisibility[field] = true;
		});
		setColumnVisibilityModel((prev) => ({ ...defaultVisibility, ...prev }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isAuthenticated || settingsLoaded) return;

		const initialize = async () => {
			const settingsMap = Object.fromEntries(
				userSettings.map((s) => [s.key, s.value])
			);
			if (settingsMap["employeeTableColumnOrder"]) {
				setColumnsOrder(
					JSON.parse(settingsMap["employeeTableColumnOrder"])
				);
			}

			if (settingsMap["employeeTableColumnSizes"]) {
				const widthArray = JSON.parse(
					settingsMap["employeeTableColumnSizes"]
				);
				const result = widthArray.reduce((acc, curr) => {
					acc[curr.field] = curr.width;
					return acc;
				}, {});
				setColumnsSize(result);
			}

			if (settingsMap["employeeTableColumnVisibility"]) {
				setColumnVisibilityModel(
					JSON.parse(settingsMap["employeeTableColumnVisibility"])
				);
			}

			if (settingsMap["employeeTableSortModel"]) {
				const savedSortModel = JSON.parse(
					settingsMap["employeeTableSortModel"]
				);
				if (Array.isArray(savedSortModel)) {
					const validSortModel = savedSortModel.filter((model) =>
						baseColumns.some((col) => col.field === model.field)
					);
					if (validSortModel.length > 0) {
						setSortModel(savedSortModel);
					}
				}
			}

			setSettingsLoaded(true);
		};

		initialize();
	}, [isAuthenticated, userSettings, settingsLoaded, baseColumns]);

	const handleSortModelChange = useCallback(
		(model) => {
			const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;

			// Avoid unnecessary state updates if sort model didn't change
			const isSortModelDifferent =
				JSON.stringify(newSortModel) !== JSON.stringify(sortModel);

			if (isSortModelDifferent) {
				setSortModel(newSortModel);
				setPage(INITIAL_PAGE);

				if (userData?.user?.ptsUserID) {
					dispatch(
						updateOrCreateUserSetting(
							userData.user.ptsUserID,
							"employeeTableSortModel",
							JSON.stringify(newSortModel)
						)
					);
				}
			}
		},
		[dispatch, userData?.user?.ptsUserID, sortModel]
	);

	const handleColumnOrderChange = useCallback(
		(params, event, details) => {
			const newColumnOrder = details.api
				.getAllColumns()
				.map((col) => col.field);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"employeeTableColumnOrder",
						JSON.stringify(newColumnOrder)
					)
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID]
	);

	const handleColumnWidthChange = (params, event, details) => {
		const columnSizes = details.api.getAllColumns().map((col) => {
			return {
				field: col.field,
				width: col.width,
			};
		});
		if (userData?.user?.ptsUserID) {
			dispatch(
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"employeeTableColumnSizes",
					JSON.stringify(columnSizes)
				)
			);
		}
	};

	const handleColumnVisibilityModelChange = useCallback(
		(newModel) => {
			setColumnVisibilityModel(newModel);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"employeeTableColumnVisibility",
						JSON.stringify(newModel)
					)
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID]
	);

	const handleDelete = useCallback(() => {
		if (selectedId) {
			dispatch(deleteEmployee(selectedId, userData?.user?.ptsUserID));
			setOpenDeleteDialog(false);
			setSelectedId(null);
		}
	}, [dispatch, selectedId, userData?.user?.ptsUserID]);

	const resetFilters = () => {
		setFilters(initialFilterState);
		dispatch({ type: AMSModules.Employees, payload: initialFilterState });
	};

	const handleOnFilterChange = (newFilters) => {
		setFilters(newFilters);
		dispatch({ type: AMSModules.Employees, payload: newFilters });
	  }

	const rowsPerPageOptions = useMemo(
		() => getRowsPerPageOptions(total),
		[total]
	);

	const isSelected = (id) => {
		const isTrue = selectedId === id;
		return isTrue ? isTrue : false;
	};

	const handleClick = (event, id) => {
		let newSelected;
		if (selectedId !== id) {
			newSelected = id;
		}
		setSelectedId(newSelected);
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 85px)",
				marginTop: theme.spacing(0.5),
			}}
		>
			<Paper
				sx={{
					display: "flex",
					flexDirection: "column",
					flex: 1,
					overflow: "hidden",
					m: 1,
					p: 2,
				}}
			>
				{canReadEmployees ? (
					<>
						<EnhancedTableToolbar
							onAddClick={() =>
								history.push(`${PATH_EMPLOYEE_ADD}`)
							}
							employees={employees}
							onFilterChange={handleOnFilterChange}
							filters={filters}
							employmentOptions={employmentOptions}
							departmentOptions={departmentOptions}
							titlePositionOptions={titlePositionOptions}
							rankOptions={rankOptions}
							sectionOptions={sectionOptions}
							countyOptions={countyOptions}
							genderOptions={genderOptions}
							raceOptions={raceOptions}
							ethnicityOptions={ethnicityOptions}
							contactInfoOptions={contactInfoOptions}
							tagTypeOptions={tagTypeOptions}
							suffixOptions={suffixOptions}
							resetFilters={resetFilters}
						/>

						<Box
							sx={{
								flex: 1,
								overflow: "hidden",
								display: "flex",
								flexDirection: "column",
								height: "100%",
							}}
						>
							{currentView === employeesView.employeeGridView && (
								<DataGridPro
									rows={employees}
									columns={columns}
									initialState={{
										pinnedColumns: { right: ["actions"] },
									}}
									getRowId={(row) => row.GUID}
									columnVisibilityModel={
										columnVisibilityModel
									}
									onColumnVisibilityModelChange={
										handleColumnVisibilityModelChange
									}
									pageSize={pageSize}
									rowsPerPageOptions={rowsPerPageOptions}
									pagination
									paginationMode="server"
									sortModel={sortModel}
									sortingMode="server"
									onSortModelChange={handleSortModelChange}
									rowCount={total}
									page={page}
									onPageChange={setPage}
									onPageSizeChange={setPageSize}
									loading={loading}
									disableSelectionOnClick
									onColumnOrderChange={
										handleColumnOrderChange
									}
									onColumnWidthChange={
										handleColumnWidthChange
									}
									sx={{
										...paginationStyle(theme, themeMode),
										color: theme.palette.colorFont,
										margin: 1,
									}}
								/>
							)}
							{currentView === employeesView.employeeCardView && (
								<EmployeeCardView
									employees={employees}
									isSelected={isSelected}
									setSelected={setSelectedId}
									handleClick={handleClick}
									setOpenDeleteDialog={setOpenDeleteDialog}
								/>
							)}
						</Box>
						{currentView === employeesView.employeeCardView && (
							<TablePagination
								page={page}
								count={total}
								rowsPerPage={pageSize}
								rowsPerPageOptions={rowsPerPageOptions}
								component="div"
								onPageChange={(e, newPage) => setPage(newPage)}
								onRowsPerPageChange={(e) => {
									setPageSize(parseInt(e.target.value, 10));
									setPage(0);
								}}
								sx={{
									...paginationStyle(theme, themeMode),
									color: theme.palette.colorFont,
									margin: 0,
									borderTop: `1px solid ${theme.palette.divider}`,
								}}
							/>
						)}

						<DeleteConfirmationDialog
							open={openDeleteDialog}
							onClose={() => {
								setOpenDeleteDialog(false);
								setSelectedId(null);
							}}
							onConfirm={handleDelete}
							title="Delete Employee"
							message="Are you sure you want to delete this employee?"
						/>
					</>
				) : (
					<Box
						sx={{
							flex: 1,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<h3>You don't have permission to view employees.</h3>
					</Box>
				)}
			</Paper>
		</Box>
	);
};

export default Employees;
